import { useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import { useToken } from '../auth/hooks'
import { BASE_URL } from '../constants'

import useRecurrantFetch from '../hooks/useRecurrantFetch'
import useFetch from '../hooks/useFetch'

import { useIsSelected } from '../tabs/hooks'

import { useMap, useEsri } from '../map/hooks'
import { useTab } from '../tabs/hooks'

import {
  fetchSuccess,
  fetchStart,
  fetchFail,
  fetchAgenciesFail,
  fetchAgenciesStart,
  fetchAgenciesSuccess,
  selectClosures,
  nextClosure,
  prevClosure,
  clearSelection,
  openEditor,
  setAgencyId,
  setNotes,
  setPending,
  setReason,
  setError,
  addClosure,
  editClosure,
  removeClosure,
  hoverSegmentStart,
  hoverSegmentEnd,
  addSegments,
  removeSegments,
} from './actions'

import {
  getClosureById,
  getClosures,
  getAgencyIds,
  getAgencies,
  getAgencyById,
  getClosureIds,
  getClosuresIsLoading,
  getCurrentSelectedClosure,
  getSelectedClosureIds,
  getCurrentSelectedIndex,
  getSelectedCount,
  getEditor,
} from './selectors'

export const useRoadClosuresFetch = () => {
  const dispatch = useDispatch()

  return useRecurrantFetch(`${BASE_URL}api/roadClosures/active`, {
    onStart: () => dispatch(fetchStart()),
    onSuccess: (closures) => dispatch(fetchSuccess(closures)),
    onError: (error) => dispatch(fetchFail(error)),
  })
}

export const useAgenciesFetch = () => {
  const dispatch = useDispatch()

  return useFetch(`${BASE_URL}api/roadClosures/agencies`, {
    onStart: () => dispatch(fetchAgenciesStart()),
    onSuccess: (agencies) => dispatch(fetchAgenciesSuccess(agencies)),
    onError: (error) => dispatch(fetchAgenciesFail(error)),
  })
}

export const useClosure = (closureId) => {
  const dispatch = useDispatch()
  const closure = useSelector(getClosureById)(closureId)

  const select = useCallback(() => dispatch(selectClosures([closureId])), [
    dispatch,
    closureId,
  ])

  return [closure, { select }]
}

export const useCreateClosure = () => {
  const dispatch = useDispatch()
  const token = useToken()
  const closure = useSelector(getEditor)
  const [{ view }] = useMap()
  const [ready, { Polyline }] = useEsri(['esri/geometry/Polyline'])
  const [_, goToList] = useTab('/roadclosures/closureList')

  useEffect(() => {
    const clearForm = () => dispatch(openEditor())
    return clearForm
  }, [dispatch])

  const actions = useMemo(
    () => ({
      setAgencyId: (agencyId) => dispatch(setAgencyId(agencyId)),
      setNotes: (notes) => dispatch(setNotes(notes)),
      setReason: (reason) => dispatch(setReason(reason)),
      setPending: (pending) => dispatch(setPending(pending)),
      create: async () => {
        if (closure.agencyId == -1) {
          dispatch(setError('Please select a Closing Agency'))
        } else if (closure.reason == -1) {
          dispatch(setError('You must select a Reason'))
        } else {
          const request = {
            reason: closure.reason,
            status: closure.pending ? 'pending' : 'active',
            notes: closure.notes,
            agencyId: closure.agencyId,
            roadSegments: closure.roadSegments.map((s) => s.id),
          }
          const response = await fetch(`${BASE_URL}api/roadClosures`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          })
          if (response.ok) {
            const roadClosure = await response.json()
            dispatch(addClosure(roadClosure))
            goToList()
          } else {
            dispatch(setError(response.statusText))
          }
        }
      },
      zoomTo: () => {
        if (ready && view && closure.roadSegments.length) {
          const segmentsGeometry = {
            type: 'polyline',
            paths: closure.roadSegments.flatMap(
              (segment) => segment.geometry.paths
            ),
          }
          view.goTo(new Polyline(segmentsGeometry))
        }
      },
    }),
    [dispatch, token, closure, view, goToList, ready, Polyline]
  )

  return [closure, actions]
}

export const useEditClosure = () => {
  const dispatch = useDispatch()
  const token = useToken()
  const closure = useSelector(getEditor)
  const [{ view }] = useMap()
  const [ready, { Polyline }] = useEsri(['esri/geometry/Polyline'])
  const [_, goToList] = useTab('/roadclosures/closureList')

  useEffect(() => {
    const clearForm = () => dispatch(openEditor())
    return clearForm
  }, [dispatch])

  const actions = useMemo(
    () => ({
      setNotes: (notes) => dispatch(setNotes(notes)),
      reOpen: async () => {
        const response = await fetch(
          `${BASE_URL}api/roadClosures/${closure.id}/reopen`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        if (response.ok) {
          dispatch(removeClosure(closure.id))
          goToList()
        } else {
          dispatch(setError(response.statusText))
        }
      },
      markAsPending: async () => {
        const response = await fetch(
          `${BASE_URL}api/roadClosures/${closure.id}/markAsPending`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        if (response.ok) {
          dispatch(editClosure({ ...closure, error: undefined }))
          goToList()
        } else {
          dispatch(setError(response.statusText))
        }
      },
      reclose: async () => {
        const response = await fetch(
          `${BASE_URL}api/roadClosures/${closure.id}/reclose`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        if (response.ok) {
          dispatch(editClosure({ ...closure, error: undefined }))
          goToList()
        } else {
          dispatch(setError(response.statusText))
        }
      },
      edit: async () => {
        if (closure.roadSegments.length <= 0) {
          dispatch(setError('A closure must have at least one road segment.'))
        } else {
          const request = {
            id: closure.id,
            notes: closure.notes,
            addRoadSegments: closure.roadSegments
              .filter((s) => s.new)
              .map((s) => s.id),
            removeRoadSegments: closure.roadSegments
              .filter((s) => s.removed)
              .map((s) => s.id),
          }
          const response = await fetch(
            `${BASE_URL}api/roadClosures/${closure.id}`,
            {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(request),
            }
          )
          if (response.ok) {
            const updatedClosure = {
              id: closure.id,
              notes: closure.notes,
              agency: closure.agency,
              agencyId: closure.agencyId,
              closedBy: closure.closedBy,
              closedDate: closure.closedDate,
              reason: closure.reason,
              status: closure.status,
              roadSegments: closure.roadSegments
                .filter((s) => !s.removed)
                .map((s) => ({
                  id: s.id,
                  name: s.name,
                  block: s.block,
                  city: s.city,
                  closedSince:
                    s.closedSince || (s.new && new Date().toISOString()),
                  closureId: closure.id,
                  geometry: s.geometry,
                })),
            }
            dispatch(editClosure(updatedClosure))
            goToList()
          } else {
            dispatch(setError(response.statusText))
          }
        }
      },
      zoomTo: () => {
        if (ready && view && closure.roadSegments.length) {
          const segmentsGeometry = {
            type: 'polyline',
            paths: closure.roadSegments.flatMap(
              (segment) => segment.geometry.paths
            ),
          }
          view.goTo(new Polyline(segmentsGeometry))
        }
      },
    }),
    [dispatch, token, closure, view, goToList, ready, Polyline]
  )

  return [closure, actions]
}

export const useSelectedClosure = () => useSelector(getCurrentSelectedClosure)

export const useSegmentList = () => {
  const [ready, { Polyline }] = useEsri(['esri/geometry/Polyline'])
  const closure = useSelectedClosure()
  const segments = closure.roadSegments || []

  const [{ view }] = useMap()
  const zoom = useCallback(
    (segment) => () => {
      ready && view && view.goTo({ target: new Polyline(segment.geometry) })
    },
    [view, ready, Polyline]
  )

  const dispatch = useDispatch()
  const actions = useMemo(
    () => ({
      onHover: (segmentId) => () =>
        dispatch(hoverSegmentStart(closure.id, segmentId)),
      onHoverEnd: (segmentId) => () =>
        dispatch(hoverSegmentEnd(closure.id, segmentId)),
      zoom,
    }),
    [dispatch, zoom, closure]
  )

  return [segments, actions]
}

export const useEditSegmentList = () => {
  const closure = useSelector(getEditor)
  const segments = closure.roadSegments || []

  const dispatch = useDispatch()
  const actions = useMemo(
    () => ({
      onHover: (segmentId) => () =>
        dispatch(hoverSegmentStart(closure.id, segmentId)),
      onHoverEnd: (segmentId) => () =>
        dispatch(hoverSegmentEnd(closure.id, segmentId)),
      add: (segment) => () => dispatch(addSegments([segment])),
      remove: (segment) => () => dispatch(removeSegments([segment.id])),
    }),
    [dispatch, closure]
  )

  return [segments, actions]
}

export const useClosures = () => {
  const closures = useSelector(getClosures)
  return closures
}

export const useClosuresList = () => {
  const unsortedIds = useSelector(getClosureIds)
  const byId = useSelector(getClosureById)
  const isLoading = useSelector(getClosuresIsLoading)

  const sortFunc = (a, b) => {
    if (moment(a.closedDate).isSame(b.closedDate)) return a.id < b.id ? -1 : 1
    return moment(a.closedDate).isBefore(b.closedDate) ? 1 : -1
  }

  const closureIds = unsortedIds
    .map(byId)
    .sort(sortFunc)
    .map((e) => e.id)

  return [{ closureIds, isLoading }]
}

export const useAgencies = () => {
  const agencies = useSelector(getAgencies)
  return agencies
}

export const useAgencyIds = () => {
  const ids = useSelector(getAgencyIds)
  return ids
}

export const useAgency = (agencyId) => {
  const agency = useSelector(getAgencyById)(agencyId)

  return [agency]
}

const REASONS = [
  'Construction',
  'Flood',
  'Washout',
  'Snow/Ice',
  'Debris',
  'Emergency',
  'Other',
]
export const useReasons = () => {
  return REASONS
}

export const useClosuresMap = () => {
  const dispatch = useDispatch()
  const selectedIds = useSelector(getSelectedClosureIds)
  const current = useSelector(getCurrentSelectedIndex)
  const closures = useSelector(getClosures)
  const addClosureSelected = useIsSelected('/roadclosures/addClosure')
  const editClosureSelected = useIsSelected('/roadclosures/editClosure')
  const editorIsOpen = addClosureSelected || editClosureSelected

  const select = useCallback(
    (closureIds) => dispatch(selectClosures(closureIds)),
    [dispatch]
  )
  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])
  const selected = useMemo(() => ({ ids: selectedIds, current }), [
    selectedIds,
    current,
  ])
  return [
    { closures, selected, editorIsOpen },
    { select, clear },
  ]
}

export const useClosuresEditMap = () => {
  const dispatch = useDispatch()
  const closure = useSelector(getEditor)
  const addClosureSelected = useIsSelected('/roadclosures/addClosure')
  const editClosureSelected = useIsSelected('/roadclosures/editClosure')
  const editorIsOpen = addClosureSelected || editClosureSelected

  const actions = useMemo(
    () => ({
      addSegments: (segments) => dispatch(addSegments(segments)),
      removeSegments: (segments) => dispatch(removeSegments(segments)),
    }),
    [dispatch]
  )

  return [{ segments: closure.roadSegments, editorIsOpen }, actions]
}

// used by ClosureDetailsPanel
export const useClosureHeader = () => {
  const dispatch = useDispatch()
  const closure = useSelector(getCurrentSelectedClosure)
  const [{ view }] = useMap()
  const [ready, { Polyline }] = useEsri(['esri/geometry/Polyline'])
  const [_, select] = useTab('/roadclosures/editClosure')

  const zoom = useCallback(() => {
    if (ready && view && closure.roadSegments.length) {
      const segmentsGeometry = {
        type: 'polyline',
        paths: closure.roadSegments.flatMap(
          (segment) => segment.geometry.paths
        ),
      }
      view.goTo(new Polyline(segmentsGeometry))
    }
  }, [view, closure, ready, Polyline])

  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])

  const edit = useCallback(() => {
    dispatch(openEditor(closure))
    select()
  }, [dispatch, closure, select])

  return { clear, zoom, edit }
}

export const useClosuresPager = () => {
  const dispatch = useDispatch()
  const incidentCount = useSelector(getSelectedCount)
  const currentIndex = useSelector(getCurrentSelectedIndex)

  const next = useCallback(() => dispatch(nextClosure()), [dispatch])
  const prev = useCallback(() => dispatch(prevClosure()), [dispatch])

  return [
    { page: currentIndex + 1, totalPages: incidentCount },
    { next, prev },
  ]
}
